/**
 * Wave E — Custom Roles UI translation bundle.
 *
 * Registered as a deep merge into the existing `translation` namespace so we
 * don't have to touch the giant pt-BR.json / en-US.json bundles. Mirrors the
 * pattern used by the `roles` namespace in `./index.ts`.
 *
 * @module shared/i18n/wave-e
 */
import i18n from 'i18next';

export const WAVE_E_PT_BR = {
  customRoles: {
    title: 'Roles Customizadas',
    subtitle: 'Crie e gerencie roles customizadas com permissões específicas',
    createButton: 'Criar nova role',
    editButton: 'Editar',
    deleteButton: 'Excluir',
    accountTab: 'Roles de Conta',
    tenantTab: 'Roles de Tenant',
    columns: {
      name: 'Nome',
      scope: 'Escopo',
      description: 'Descrição',
      permissionCount: 'Permissões',
      grantCount: 'Grants ativos',
      createdAt: 'Criado em',
      actions: 'Ações',
    },
    empty: {
      title: 'Nenhuma role customizada',
      description: 'Crie sua primeira role customizada para começar.',
    },
    legacyBanner:
      'Wave A (custom roles) requer ativação por admin. Entre em contato com o suporte.',
    editor: {
      titleCreate: 'Criar nova role',
      titleEdit: 'Editar role',
      stepIdentity: 'Identidade',
      stepPermissions: 'Permissões',
      stepReview: 'Revisão',
      scopeLabel: 'Escopo',
      scopeAccount: 'Conta',
      scopeTenant: 'Tenant',
      nameLabel: 'Nome',
      namePlaceholder: 'ex.: gerente_vendas',
      nameHelp: 'Apenas letras, números, _ e -. Mínimo 3, máximo 64 caracteres.',
      descriptionLabel: 'Descrição',
      descriptionPlaceholder: 'O que essa role faz?',
      permissionsTitle: 'Permissões',
      permissionsHelp:
        'Selecione as permissões que essa role concederá. Agrupadas por domínio.',
      selectAllInGroup: 'Selecionar tudo no grupo',
      reviewTitle: 'Revisão',
      reviewSummary: 'Confira os detalhes antes de confirmar.',
      confirm: 'Confirmar',
      next: 'Avançar',
      back: 'Voltar',
      cancel: 'Cancelar',
      saved: 'Role salva com sucesso',
      saveError: 'Falha ao salvar role',
    },
    delete: {
      confirmTitle: 'Excluir role',
      confirmBody: 'Tem certeza que deseja excluir esta role? Esta ação é irreversível.',
      cannotDelete: 'Não é possível excluir role com grants ativos',
      success: 'Role excluída',
      error: 'Falha ao excluir role',
    },
    usage: {
      title: 'Uso da role',
      activeGrants: 'Grants ativos',
      noGrants: 'Nenhum grant ativo',
      cannotDeleteBadge: 'Não é possível excluir',
    },
    catalog: {
      title: 'Catálogo de Permissões',
      subtitle: 'Lista somente-leitura de todas as permissões disponíveis',
      searchPlaceholder: 'Buscar permissão...',
      domainPlatform: 'Platform',
      domainAccount: 'Account',
      domainTenant: 'Tenant',
    },
    sidebar: {
      account: 'Roles Customizadas (Conta)',
      tenant: 'Roles Customizadas (Tenant)',
      catalog: 'Catálogo de Permissões',
    },
    permissions: {
      readonly: 'Permissão necessária',
      noAccess: 'Você não tem permissão para acessar esta página.',
    },
  },
} as const;

export const WAVE_E_EN_US = {
  customRoles: {
    title: 'Custom Roles',
    subtitle: 'Create and manage custom roles with specific permissions',
    createButton: 'Create new role',
    editButton: 'Edit',
    deleteButton: 'Delete',
    accountTab: 'Account roles',
    tenantTab: 'Tenant roles',
    columns: {
      name: 'Name',
      scope: 'Scope',
      description: 'Description',
      permissionCount: 'Permissions',
      grantCount: 'Active grants',
      createdAt: 'Created at',
      actions: 'Actions',
    },
    empty: {
      title: 'No custom roles yet',
      description: 'Create your first custom role to get started.',
    },
    legacyBanner: 'Wave A (custom roles) requires admin enable. Contact support.',
    editor: {
      titleCreate: 'Create new role',
      titleEdit: 'Edit role',
      stepIdentity: 'Identity',
      stepPermissions: 'Permissions',
      stepReview: 'Review',
      scopeLabel: 'Scope',
      scopeAccount: 'Account',
      scopeTenant: 'Tenant',
      nameLabel: 'Name',
      namePlaceholder: 'e.g. sales_manager',
      nameHelp: 'Only letters, numbers, _ and -. Min 3, max 64 chars.',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'What does this role do?',
      permissionsTitle: 'Permissions',
      permissionsHelp: 'Select the permissions this role will grant. Grouped by domain.',
      selectAllInGroup: 'Select all in group',
      reviewTitle: 'Review',
      reviewSummary: 'Review the details before confirming.',
      confirm: 'Confirm',
      next: 'Next',
      back: 'Back',
      cancel: 'Cancel',
      saved: 'Role saved successfully',
      saveError: 'Failed to save role',
    },
    delete: {
      confirmTitle: 'Delete role',
      confirmBody: 'Are you sure you want to delete this role? This action is irreversible.',
      cannotDelete: 'Cannot delete role with active grants',
      success: 'Role deleted',
      error: 'Failed to delete role',
    },
    usage: {
      title: 'Role usage',
      activeGrants: 'Active grants',
      noGrants: 'No active grants',
      cannotDeleteBadge: 'Cannot delete',
    },
    catalog: {
      title: 'Permissions Catalog',
      subtitle: 'Read-only list of all available permissions',
      searchPlaceholder: 'Search permission...',
      domainPlatform: 'Platform',
      domainAccount: 'Account',
      domainTenant: 'Tenant',
    },
    sidebar: {
      account: 'Custom Roles (Account)',
      tenant: 'Custom Roles (Tenant)',
      catalog: 'Permissions Catalog',
    },
    permissions: {
      readonly: 'Permission required',
      noAccess: 'You do not have permission to access this page.',
    },
  },
} as const;

let registered = false;

/**
 * Registers Wave E translations into the i18next runtime. Idempotent —
 * called from the i18n bootstrap (`./index.ts`) right after the legacy
 * bundles + roles namespace are wired up.
 */
export function registerWaveETranslations(): void {
  if (registered) return;
  i18n.addResourceBundle('pt-BR', 'translation', WAVE_E_PT_BR, /* deep */ true, /* overwrite */ true);
  i18n.addResourceBundle('en-US', 'translation', WAVE_E_EN_US, /* deep */ true, /* overwrite */ true);
  registered = true;
}
